import * as React from 'react';
import AddSearchUI from 'addsearch-search-ui';

import { Filter, SortByOption } from './AddSearchUiIntegration';

interface AddSearchUiProps {
    readonly addSearchUi: any;
    readonly searchFieldMaxWidthInPixels?: number;
    readonly searchFieldHeaderText?: string;
    readonly searchButtonText?: string;
    readonly searchInputPlaceholderText: string;
    readonly hasSearchInputAutofocus?: boolean;
    readonly hasSearchAsYouType?: boolean;
    readonly minLengthToShowSearchAsYouTypeResults?: number;
    readonly hasSearchInputFieldIcon?: boolean;
    readonly hasAutocomplete: boolean;
    readonly hasAiAnswers: boolean;
    readonly loadMoreResults: 'button' | 'pagination';
    readonly sortByOptions: SortByOption[];
    readonly filters: Filter[];
    readonly hasActiveFilters: boolean;
    readonly hasAiAnswersHideToggle?: boolean;
}

export const AddSearchUi: React.FC<AddSearchUiProps> = ({
    addSearchUi,
    searchFieldMaxWidthInPixels,
    searchFieldHeaderText,
    searchInputPlaceholderText,
    searchButtonText,
    hasSearchInputAutofocus,
    hasSearchAsYouType,
    minLengthToShowSearchAsYouTypeResults,
    hasSearchInputFieldIcon,
    hasAutocomplete,
    hasAiAnswers,
    loadMoreResults,
    sortByOptions,
    filters,
    hasActiveFilters
}) => {
    const [showMobileFilters, setShowMobileFilters] = React.useState(false);
    // Container Elements need to be available in the DOM for the the Search UI Elements to be rendered into them.

    const searchResultsContainerRef = React.useRef<HTMLDivElement>(null);

    const standardFilters = React.useMemo(() => {
        return filters.filter(
            (filter) =>
                filter.type === 'checkbox-group' ||
                filter.type === 'radio-group' ||
                filter.type === 'tags'
        );
    }, [filters]);

    const showStandardFilterView = React.useMemo(
        () => standardFilters.length > 0,
        [standardFilters]
    );

    const hasTabsFilter = React.useMemo(
        () => filters && filters.find((filter) => filter.type === 'tabs'),
        []
    );

    const hasSelectListFilter = React.useMemo(
        () => filters && filters.find((filter) => filter.type === 'select-list'),
        []
    );

    React.useEffect(() => {
        addSearchUi.searchField({
            containerId: 'search-field-container',
            placeholder: searchInputPlaceholderText,
            button: searchButtonText,
            icon: hasSearchInputFieldIcon,
            autofocus: hasSearchInputAutofocus,
            searchAsYouType: hasSearchAsYouType,
            minLengthToShowResults: minLengthToShowSearchAsYouTypeResults
        });

        if (hasAiAnswers) {
            addSearchUi.aiAnswersResult({
                containerId: 'conversational-search-result-container',
                mainHeadlineText: 'AI-generated answer',
                answerMaxHeight: 150,
                sourcesHeadlineText: 'Based on the following sources:'
            });
        }

        addSearchUi.searchResults({
            containerId: 'search-results-container'
        });

        if (hasAutocomplete) {
            addSearchUi.autocomplete({
                containerId: 'autocomplete-container',
                sources: [
                    {
                        type: AddSearchUI.AUTOCOMPLETE_TYPE.SUGGESTIONS
                    }
                ]
            });
        }

        if (loadMoreResults === 'button') {
            addSearchUi.loadMore({
                containerId: 'loadmore-container',
                type: AddSearchUI.LOAD_MORE_TYPE.BUTTON
            });
        } else if (loadMoreResults === 'pagination') {
            addSearchUi.pagination({
                containerId: 'pagination-container'
            });
        } else if (loadMoreResults === 'scroll') {
            // TODO get the infinite scroll to work
            addSearchUi.loadMore({
                containerId: searchResultsContainerRef.current,
                type: AddSearchUI.LOAD_MORE_TYPE.INFINITE_SCROLL,
                infiniteScrollElement: searchResultsContainerRef.current
            });
        }

        if (sortByOptions?.length && sortByOptions?.length > 0) {
            addSearchUi.sortBy({
                containerId: 'sortby-container',
                options: sortByOptions
            });
        }

        if (filters && filters.length > 0) {
            filters.forEach((filter, index) => {
                if (filter.type === 'checkbox-group') {
                    addSearchUi.filters({
                        containerId: `filters-container-${index}`,
                        type: AddSearchUI.FILTER_TYPE.CHECKBOX_GROUP,
                        options: filter.options
                    });
                } else if (filter.type === 'radio-group') {
                    addSearchUi.filters({
                        containerId: `filters-container-${index}`,
                        type: AddSearchUI.FILTER_TYPE.RADIO_GROUP,
                        options: filter.options
                    });
                } else if (filter.type === 'tags') {
                    addSearchUi.filters({
                        containerId: `filters-container-${index}`,
                        type: AddSearchUI.FILTER_TYPE.TAGS,
                        options: filter.options
                    });
                } else if (filter.type === 'tabs') {
                    // Supports only one tabs filter
                    addSearchUi.filters({
                        containerId: 'tab-filters-container',
                        type: AddSearchUI.FILTER_TYPE.TABS,
                        options: filter.options
                    });
                } else if (filter.type === 'select-list') {
                    // Supports only one tabs filter
                    addSearchUi.filters({
                        containerId: 'select-list-filters-container',
                        type: AddSearchUI.FILTER_TYPE.SELECT_LIST,
                        options: filter.options
                    });

                    addSearchUi.filters({
                        containerId: 'select-list-filters-container-mobile',
                        type: AddSearchUI.FILTER_TYPE.SELECT_LIST,
                        options: filter.options
                    });
                }
            });

            if (hasActiveFilters) {
                addSearchUi.activeFilters({
                    containerId: 'active-filters-container'
                });
            }
        }

        addSearchUi.start();
    }, [
        showMobileFilters,
        searchResultsContainerRef.current,
        filters,
        sortByOptions,
        hasActiveFilters,
        loadMoreResults,
        hasActiveFilters,
        hasAutocomplete,
        hasSearchAsYouType,
        hasSearchInputAutofocus,
        hasSearchInputFieldIcon,
        searchButtonText,
        searchInputPlaceholderText,
        searchFieldHeaderText
    ]);

    return (
        <div id="addsearch-ui-container" className="flex flex-col p-4 w-full h-full">
            {
                <div className="flex items-center gap-4">
                    {searchFieldHeaderText && (
                        <h2 className="text-xl font-semibold">{searchFieldHeaderText}</h2>
                    )}
                    <div
                        style={{ maxWidth: `${searchFieldMaxWidthInPixels}px` }}
                        id="search-field-container"
                    ></div>
                </div>
            }

            <div id="autocomplete-container"></div>

            <div id="conversational-search-result-container"></div>

            {hasTabsFilter && (
                <div className="mt-10 flex justify-center">
                    <div id="tab-filters-container"></div>
                </div>
            )}

            {showStandardFilterView && (
                <div
                    className={`flex flex-col md:hidden mt-10 border border-gray-300 rounded-md ${showMobileFilters ? 'bg-gray-50' : ''}`}
                >
                    <button
                        onClick={() => setShowMobileFilters(showMobileFilters ? false : true)}
                        className="flex items-center justify-between w-full text-sm md:text-base p-2"
                    >
                        <div className="flex items-center gap-2">
                            <svg
                                width="24"
                                height="24"
                                viewBox="0 0 24 24"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <path
                                    d="M20 5.6C20 5.04 20 4.76 19.89 4.546C19.7944 4.358 19.6418 4.20504 19.454 4.109C19.24 4 18.96 4 18.4 4H5.6C5.04 4 4.76 4 4.546 4.109C4.35785 4.20487 4.20487 4.35785 4.109 4.546C4 4.76 4 5.04 4 5.6V6.337C4 6.582 4 6.704 4.028 6.819C4.05249 6.92138 4.09299 7.01925 4.148 7.109C4.209 7.209 4.296 7.296 4.468 7.469L9.531 12.531C9.704 12.704 9.791 12.791 9.852 12.891C9.90733 12.9817 9.94733 13.0783 9.972 13.181C10 13.295 10 13.416 10 13.655V18.411C10 19.268 10 19.697 10.18 19.955C10.2581 20.0666 10.3582 20.161 10.4741 20.2326C10.59 20.3041 10.7192 20.3512 10.854 20.371C11.165 20.417 11.549 20.226 12.315 19.842L13.115 19.442C13.437 19.282 13.597 19.202 13.714 19.082C13.8178 18.976 13.8967 18.8483 13.945 18.708C14 18.55 14 18.37 14 18.011V13.663C14 13.418 14 13.296 14.028 13.181C14.0525 13.0786 14.093 12.9807 14.148 12.891C14.208 12.791 14.295 12.705 14.465 12.535L14.469 12.531L19.532 7.469C19.704 7.296 19.79 7.209 19.852 7.109C19.9073 7.01833 19.9473 6.92167 19.972 6.819C20 6.706 20 6.584 20 6.345V5.6Z"
                                    stroke="#8D8D8D"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                />
                            </svg>
                            Filter by
                        </div>
                        <svg
                            className={`${showMobileFilters ? 'transform rotate-180' : ''}`}
                            width="16"
                            height="16"
                            viewBox="0 0 16 16"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <path
                                fillRule="evenodd"
                                clipRule="evenodd"
                                d="M1.64604 4.64601C1.69249 4.59945 1.74766 4.5625 1.80841 4.5373C1.86915 4.51209 1.93427 4.49911 2.00004 4.49911C2.06581 4.49911 2.13093 4.51209 2.19167 4.5373C2.25242 4.5625 2.30759 4.59945 2.35404 4.64601L8.00004 10.293L13.646 4.64601C13.6925 4.59952 13.7477 4.56264 13.8085 4.53749C13.8692 4.51233 13.9343 4.49938 14 4.49938C14.0658 4.49938 14.1309 4.51233 14.1916 4.53749C14.2524 4.56264 14.3076 4.59952 14.354 4.64601C14.4005 4.6925 14.4374 4.74769 14.4626 4.80842C14.4877 4.86916 14.5007 4.93426 14.5007 5.00001C14.5007 5.06575 14.4877 5.13085 14.4626 5.19159C14.4374 5.25233 14.4005 5.30752 14.354 5.35401L8.35404 11.354C8.30759 11.4006 8.25242 11.4375 8.19167 11.4627C8.13093 11.4879 8.06581 11.5009 8.00004 11.5009C7.93427 11.5009 7.86915 11.4879 7.80841 11.4627C7.74766 11.4375 7.69248 11.4006 7.64604 11.354L1.64604 5.35401C1.59948 5.30756 1.56253 5.25239 1.53733 5.19164C1.51212 5.1309 1.49915 5.06578 1.49915 5.00001C1.49915 4.93424 1.51212 4.86912 1.53733 4.80837C1.56253 4.74763 1.59948 4.69245 1.64604 4.64601Z"
                                fill="#3A3A3A"
                            />
                        </svg>
                    </button>
                    {showMobileFilters && (
                        <div className="p-4 flex flex-col gap-6">
                            {standardFilters.map((filter, index) => (
                                <div key={filter.type}>
                                    {filter.labelText && (
                                        <p className="mb-3 pb-1 border-b border-gray-200 text-sm font-semibold">
                                            {filter.labelText}
                                        </p>
                                    )}
                                    <div id={`filters-container-${index}`}></div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {hasSelectListFilter && (
                <div className="mt-10 w-full flex justify-end">
                    <div className="md:hidden" id="select-list-filters-container-mobile"></div>
                </div>
            )}

            <div className="mt-2 md:mt-8 flex flex-col items-end md:flex-row md:items-center md:justify-between gap-2">
                {hasActiveFilters && (
                    <div>
                        <div id="active-filters-container"></div>
                    </div>
                )}
                {(hasSelectListFilter || (sortByOptions?.length && sortByOptions.length > 0)) && (
                    <div className="mt-2 flex items-center justify-end gap-2">
                        {hasSelectListFilter && (
                            <div
                                className="hidden md:flex"
                                id="select-list-filters-container"
                            ></div>
                        )}
                        {sortByOptions?.length && sortByOptions.length > 0 && (
                            <div id="sortby-container"></div>
                        )}
                    </div>
                )}
            </div>

            <div className="mt-4">
                {showStandardFilterView ? (
                    <div className="grid grid-cols-1 gap-2 md:grid-cols-1-4 md:gap-6">
                        <div className="mt-4 hidden md:flex flex-col gap-6">
                            {standardFilters.map((filter, index) => (
                                <div key={filter.type}>
                                    {filter.labelText && (
                                        <p className="mb-3 pb-1 border-b border-gray-200 text-sm font-semibold">
                                            {filter.labelText}
                                        </p>
                                    )}
                                    <div id={`filters-container-${index}`}></div>
                                </div>
                            ))}
                        </div>
                        <div className="w-full h-full">
                            <div className="h-[500px]">
                                <div
                                    ref={searchResultsContainerRef}
                                    id="search-results-container"
                                ></div>
                            </div>
                            <div className="flex justify-center">
                                <div id="loadmore-container"></div>
                            </div>
                            <div id="pagination-container"></div>
                        </div>
                    </div>
                ) : (
                    <div className="flex">
                        <div className="w-full h-full">
                            <div className="h-[500px]">
                                <div
                                    ref={searchResultsContainerRef}
                                    id="search-results-container"
                                ></div>
                            </div>
                            <div className="flex justify-center">
                                <div id="loadmore-container"></div>
                            </div>
                            <div id="pagination-container"></div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};
